import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { OfferItem } from '@otw/models/offers';

interface OfferState {
  items: OfferItem[];
  editingOfferId: string | null;
  editingOfferName: string | null;
  editingOfferNotes: string | null;
}

type OfferAction =
  | { type: 'ADD_ITEM'; payload: Omit<OfferItem, 'id' | 'addedAt'> }
  | { type: 'REMOVE_ITEM'; payload: string }
  | { type: 'UPDATE_ITEM'; payload: OfferItem }
  | { type: 'CLEAR_ITEMS' }
  | { type: 'SET_EDITING_OFFER'; payload: { id: string; name: string; notes: string; items?: OfferItem[] } | null }
  | { type: 'CLEAR_EDITING_OFFER' }
  | { type: 'LOAD_ITEMS'; payload: OfferItem[] };

const initialState: OfferState = {
  items: [],
  editingOfferId: null,
  editingOfferName: null,
  editingOfferNotes: null,
};

const offerReducer = (state: OfferState, action: OfferAction): OfferState => {
  switch (action.type) {
    case 'ADD_ITEM':
      const newItem: OfferItem = {
        ...action.payload,
        id: `item-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        addedAt: new Date().toISOString(),
      };
      return {
        ...state,
        items: [...state.items, newItem],
      };

    case 'REMOVE_ITEM':
      return {
        ...state,
        items: state.items.filter(item => item.id !== action.payload),
      };

    case 'UPDATE_ITEM':
      return {
        ...state,
        items: state.items.map(item => 
          item.id === action.payload.id ? action.payload : item
        ),
      };

    case 'CLEAR_ITEMS':
      return {
        ...state,
        items: [],
      };

    case 'LOAD_ITEMS':
      return {
        ...state,
        items: action.payload,
      };

    case 'SET_EDITING_OFFER':
      if (action.payload) {
        return {
          ...state,
          editingOfferId: action.payload.id,
          editingOfferName: action.payload.name,
          editingOfferNotes: action.payload.notes,
          items: action.payload.items || state.items,
        };
      } else {
        return {
          ...state,
          editingOfferId: null,
          editingOfferName: null,
          editingOfferNotes: null,
        };
      }

    case 'CLEAR_EDITING_OFFER':
      return {
        ...state,
        editingOfferId: null,
        editingOfferName: null,
        editingOfferNotes: null,
      };

    default:
      return state;
  }
};

interface OfferContextType {
  state: OfferState;
  addItem: (item: Omit<OfferItem, 'id' | 'addedAt'>) => void;
  removeItem: (id: string) => void;
  updateItem: (item: OfferItem) => void;
  clearItems: () => void;
  setEditingOffer: (offer: { id: string; name: string; notes: string; items?: OfferItem[] } | null) => void;
  clearEditingOffer: () => void;
  loadItems: (items: OfferItem[]) => void;
  getTotalAmount: () => number;
}

const OfferContext = createContext<OfferContextType | undefined>(undefined);

export const OfferProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(offerReducer, initialState);

  const addItem = (item: Omit<OfferItem, 'id' | 'addedAt'>) => {
    dispatch({ type: 'ADD_ITEM', payload: item });
  };

  const removeItem = (id: string) => {
    dispatch({ type: 'REMOVE_ITEM', payload: id });
  };

  const updateItem = (item: OfferItem) => {
    dispatch({ type: 'UPDATE_ITEM', payload: item });
  };

  const clearItems = () => {
    dispatch({ type: 'CLEAR_ITEMS' });
  };

  const loadItems = (items: OfferItem[]) => {
    dispatch({ type: 'LOAD_ITEMS', payload: items });
  };

  const setEditingOffer = (offer: { id: string; name: string; notes: string; items?: OfferItem[] } | null) => {
    dispatch({ type: 'SET_EDITING_OFFER', payload: offer });
  };

  const clearEditingOffer = () => {
    dispatch({ type: 'CLEAR_EDITING_OFFER' });
  };

  const getTotalAmount = () => {
    return state.items.reduce((total, item) => {
      let itemPrice = 0;
      if (item.selectedPrice === 'retail') {
        itemPrice = item.retailPrice;
      } else if (item.selectedPrice === 'wholesale') {
        itemPrice = item.wholesalePrice;
      } else if (item.selectedPrice === 'inventory') {
        itemPrice = item.inventoryPrice || item.retailPrice;
      }
      return total + itemPrice;
    }, 0);
  };

  const value: OfferContextType = {
    state,
    addItem,
    removeItem,
    updateItem,
    clearItems,
    setEditingOffer,
    clearEditingOffer,
    loadItems,
    getTotalAmount,
  };

  return (
    <OfferContext.Provider value={value}>
      {children}
    </OfferContext.Provider>
  );
};

export const useOfferContext = () => {
  const context = useContext(OfferContext);
  if (context === undefined) {
    throw new Error('useOfferContext must be used within an OfferProvider');
  }
  return context;
}; 